import React, { useEffect, useState } from "react";
import api from "../api/client";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Plus, X, Globe, Save, Building2, Car, Layers, Maximize, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";
import MediaGallery from "./MediaGallery";

const ICON_OPTIONS = [
  { key: "building", icon: Building2, label: "Bâtiment" },
  { key: "car", icon: Car, label: "Parking" },
  { key: "layers", icon: Layers, label: "Étages/Unités" },
  { key: "maximize", icon: Maximize, label: "Surface" },
  { key: "sparkles", icon: Sparkles, label: "Premium" },
];

const SUGGESTED_AMENITIES = [
  "Piscine", "Ascenseur", "Salle de sport", "Réception 24/7", "Sécurité 24/7",
  "Parking sous-sol", "Vue mer", "Vue panoramique", "Jardin privatif",
  "Climatisation", "Wifi", "Salon d'extérieur", "Bar lounge", "Salle de réunion",
];

export default function PublicationPanel({ immeuble, onUpdate }) {
  const [form, setForm] = useState({
    slug: "",
    slogan: "",
    description_longue: "",
    amenities: [],
    key_figures: [],
    typologies: [],
    is_published: false,
  });
  const [saving, setSaving] = useState(false);
  const [newAmenity, setNewAmenity] = useState("");
  const [typoUploading, setTypoUploading] = useState(null);

  useEffect(() => {
    if (!immeuble) return;
    setForm({
      slug: immeuble.slug || "",
      slogan: immeuble.slogan || "",
      description_longue: immeuble.description_longue || "",
      amenities: immeuble.amenities || [],
      key_figures: immeuble.key_figures && immeuble.key_figures.length > 0 ? immeuble.key_figures : [
        { label: "Surface totale", value: "", icon: "maximize" },
        { label: "Appartements", value: "", icon: "building" },
        { label: "Places de parking", value: "", icon: "car" },
      ],
      typologies: immeuble.typologies || [],
      is_published: !!immeuble.is_published,
    });
  }, [immeuble]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        slug: form.slug.trim() || null,
        slogan: form.slogan,
        description_longue: form.description_longue,
        amenities: form.amenities.filter(Boolean),
        key_figures: form.key_figures.filter((k) => k.label && k.value !== ""),
        typologies: form.typologies.filter((t) => t.code || t.nom),
        is_published: form.is_published,
      };
      await api.patch(`/immeubles/${immeuble.id}`, payload);
      toast.success("Publication mise à jour");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const toggleAmenity = (a) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  };

  const addCustomAmenity = () => {
    const a = newAmenity.trim();
    if (!a) return;
    if (!form.amenities.includes(a)) {
      setForm({ ...form, amenities: [...form.amenities, a] });
    }
    setNewAmenity("");
  };

  const updateFigure = (i, field, val) => {
    setForm((f) => {
      const arr = [...f.key_figures];
      arr[i] = { ...arr[i], [field]: val };
      return { ...f, key_figures: arr };
    });
  };
  const addFigure = () => setForm((f) => ({ ...f, key_figures: [...f.key_figures, { label: "", value: "", icon: "sparkles" }] }));
  const removeFigure = (i) => setForm((f) => ({ ...f, key_figures: f.key_figures.filter((_, idx) => idx !== i) }));

  return (
    <form onSubmit={save} className="space-y-6">
      <div className="flex items-start justify-between p-4 rounded-md border border-[#E2E5F1] bg-[#F8FAFC]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#E8EBFA] text-[#1E3A8A] flex items-center justify-center">
            <Globe className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display text-lg font-bold text-[#0F1A3D]">Site web public</div>
            <div className="text-xs text-[#4B5775]">Activez la publication pour exposer ce projet sur tpco.sn.</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-[#0F1A3D]">{form.is_published ? "Publié" : "Brouillon"}</span>
          <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="publish-toggle" />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">URL publique (slug)</Label>
          <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="ex: residence-taif" data-testid="publish-slug" />
          <div className="text-[10px] text-[#4B5775] mt-1">URL : /projet/<span className="font-mono font-semibold">{form.slug || "auto-généré"}</span></div>
        </div>
        <div>
          <Label className="text-xs">Slogan / Accroche</Label>
          <Input value={form.slogan} onChange={(e) => setForm({ ...form, slogan: e.target.value })} placeholder="ex: Résidence exclusive front de mer" data-testid="publish-slogan" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Description longue (publique)</Label>
        <textarea
          value={form.description_longue}
          onChange={(e) => setForm({ ...form, description_longue: e.target.value })}
          placeholder="Décrivez le projet en détail : style, vue, équipements, environnement…"
          rows={6}
          className="w-full text-sm px-3 py-2 rounded border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] resize-none"
          data-testid="publish-description"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-wider">Chiffres clés</Label>
          <button type="button" onClick={addFigure} className="text-xs text-[#1E3A8A] font-semibold inline-flex items-center gap-1 hover:underline" data-testid="publish-add-figure">
            <Plus className="w-3 h-3" /> Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {form.key_figures.map((kf, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_auto_auto] gap-2 items-center">
              <Input value={kf.label} onChange={(e) => updateFigure(i, "label", e.target.value)} placeholder="ex: Surface totale" />
              <Input value={kf.value} onChange={(e) => updateFigure(i, "value", e.target.value)} placeholder="ex: 2000 m²" />
              <select
                value={kf.icon}
                onChange={(e) => updateFigure(i, "icon", e.target.value)}
                className="h-9 text-xs px-2 rounded border border-[#E2E5F1] bg-white"
              >
                {ICON_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <button type="button" onClick={() => removeFigure(i)} className="p-2 text-[#C83B3B] hover:bg-[#FBEAE9] rounded"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider mb-2 block">Équipements & Aménités</Label>
        <div className="flex flex-wrap gap-2 mb-3">
          {SUGGESTED_AMENITIES.map((a) => {
            const active = form.amenities.includes(a);
            return (
              <button
                key={a}
                type="button"
                onClick={() => toggleAmenity(a)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active ? "bg-[#1E3A8A] border-[#1E3A8A] text-white" : "bg-white border-[#E2E5F1] text-[#4B5775] hover:border-[#1E3A8A] hover:text-[#1E3A8A]"}`}
                data-testid={`amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {a}
              </button>
            );
          })}
          {form.amenities.filter((a) => !SUGGESTED_AMENITIES.includes(a)).map((a) => (
            <span key={a} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#FCE4EC] text-[#E91E63] border border-[#F8BBD0]">
              {a}
              <button type="button" onClick={() => toggleAmenity(a)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 max-w-md">
          <Input value={newAmenity} onChange={(e) => setNewAmenity(e.target.value)} placeholder="Ajouter un équipement personnalisé" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomAmenity())} />
          <button type="button" onClick={addCustomAmenity} className="h-10 px-3 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-semibold">Ajouter</button>
        </div>
      </div>

      <div>
        <div className="label-tiny mb-3">Photos du projet</div>
        <MediaGallery ownerType="immeuble" ownerId={immeuble.id} onCoverChange={onUpdate} />
      </div>

      {/* --- TYPOLOGIES DE VILLA (T1 / T2 / T3 ...) --- */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-tiny">Typologies de villa</div>
            <p className="text-xs text-[#4B5775] mt-1">Une carte visuelle par type de villa (image, surface, description). Affiché sur la fiche publique du projet.</p>
            <p className="text-[11px] text-[#1E3A8A] mt-1 font-semibold" data-testid="typologies-stock-note">Métadonnées d'affichage uniquement — le nombre réel de logements se gère dans « Unités &amp; Plan › Gestion du stock physique ».</p>
          </div>
          <button
            type="button"
            data-testid="add-typologie-btn"
            onClick={() => setForm((f) => ({ ...f, typologies: [...(f.typologies || []), { code: `T${(f.typologies || []).length + 1}`, nom: "", surface: "", nb_pieces: "", niveau: "", description: "" }] }))}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter un type
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(form.typologies || []).map((t, idx) => (
            <div key={idx} className="border border-[#E2E5F1] rounded-lg p-4 bg-[#F8FAFC] space-y-3" data-testid={`typologie-card-${idx}`}>
              <div className="flex items-center justify-between">
                <Input
                  value={t.code || ""}
                  onChange={(e) => setForm((f) => {
                    const arr = [...f.typologies]; arr[idx] = { ...arr[idx], code: e.target.value.toUpperCase() };
                    return { ...f, typologies: arr };
                  })}
                  placeholder="T1"
                  className="w-24 h-9 font-mono font-bold text-center"
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, typologies: f.typologies.filter((_, i) => i !== idx) }))}
                  className="text-[#C83B3B] hover:bg-[#FBEAE9] p-1.5 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Image */}
              <div className="relative aspect-video rounded-md overflow-hidden bg-[#E2E5F1] border border-dashed border-[#9CA3B5]">
                {t.image_url ? (
                  <img
                    src={t.image_url.startsWith("http") ? t.image_url : `${process.env.REACT_APP_BACKEND_URL}${t.image_url}`}
                    alt={t.code}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-[#9CA3B5]">Pas d&apos;image</div>
                )}
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition cursor-pointer">
                  <span className="text-white text-xs font-bold inline-flex items-center gap-1.5">
                    {typoUploading === idx ? "Upload…" : (t.image_url ? "Changer l'image" : "Uploader une image")}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    data-testid={`typologie-upload-${idx}`}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !t.code) { toast.error("Renseignez d'abord le code (T1, T2…)"); return; }
                      setTypoUploading(idx);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        const { data } = await api.post(`/immeubles/${immeuble.id}/typologies/${t.code}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                        // reload typologies list from response
                        setForm((f) => ({ ...f, typologies: data.typologies }));
                        toast.success("Image mise à jour");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Erreur upload");
                      } finally { setTypoUploading(null); }
                    }}
                  />
                </label>
              </div>

              <Input
                value={t.nom || ""}
                onChange={(e) => setForm((f) => {
                  const arr = [...f.typologies]; arr[idx] = { ...arr[idx], nom: e.target.value };
                  return { ...f, typologies: arr };
                })}
                placeholder="Nom (ex: Villa Type 1)"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={t.surface || ""}
                  onChange={(e) => setForm((f) => {
                    const arr = [...f.typologies]; arr[idx] = { ...arr[idx], surface: e.target.value };
                    return { ...f, typologies: arr };
                  })}
                  placeholder="Surface m²"
                  type="number"
                />
                <Input
                  value={t.nb_pieces || ""}
                  onChange={(e) => setForm((f) => {
                    const arr = [...f.typologies]; arr[idx] = { ...arr[idx], nb_pieces: e.target.value };
                    return { ...f, typologies: arr };
                  })}
                  placeholder="Nb pièces"
                  type="number"
                />
                <Input
                  value={t.niveau || ""}
                  onChange={(e) => setForm((f) => {
                    const arr = [...f.typologies]; arr[idx] = { ...arr[idx], niveau: e.target.value };
                    return { ...f, typologies: arr };
                  })}
                  placeholder="Niveau (R+1)"
                />
              </div>
              <textarea
                value={t.description || ""}
                onChange={(e) => setForm((f) => {
                  const arr = [...f.typologies]; arr[idx] = { ...arr[idx], description: e.target.value };
                  return { ...f, typologies: arr };
                })}
                placeholder="Description courte (4 chambres, salon, terrasse…)"
                rows={2}
                className="w-full text-xs rounded-md border border-[#E2E5F1] p-2 bg-white resize-none"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-4 border-t border-[#E2E5F1]">
        <button type="submit" disabled={saving} data-testid="publish-save-btn" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-sm font-semibold disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer la publication"}
        </button>
        {form.is_published && form.slug && (
          <a
            href={`/site/projet/${form.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="publish-preview-link"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E2E5F1] hover:border-[#1E3A8A] text-[#1E3A8A] text-sm font-semibold"
          >
            <Eye className="w-4 h-4" /> Aperçu public
          </a>
        )}
      </div>
    </form>
  );
}
